/**
 * Kortingscodes (coupons). Beheerd in de admin, opgeslagen in Firestore `coupons/{CODE}`.
 *
 * De code zelf is niets waard: de klant tikt 'm in, maar álle rekenwerk gebeurt op de server
 * (`createOrder`) met exact de functies hieronder. De checkout gebruikt dezelfde functies via
 * een callable, puur om te laten zien wat de code oplevert — nooit om het bedrag te bepalen.
 *
 * Afspraken (vastgelegd met Thijs):
 *  - drie soorten: percentage, vast bedrag, gratis verzending;
 *  - een code stapelt NIET met een klant-/staffelkorting: de gunstigste van de twee wint;
 *  - beperkingen per code: geldig tot een datum, minimaal bestelbedrag, één keer per klant.
 *
 * Bedragen zijn integer eurocenten EXCL. btw, net als de rest van de prijsketen.
 */
import type { Cents } from "./types/money";
import type { OrderDiscount } from "./types/order";
import { discountAmount } from "./pricing/shipping";

export type CouponKind = "percent" | "amount" | "freeShipping";

export interface Coupon {
  /** De code in hoofdletters (= Firestore doc-id), bv. "WELKOM10". */
  code: string;
  kind: CouponKind;
  /** Percentage (1–100) bij "percent", bedrag in centen excl. btw bij "amount". Leeg bij gratis verzending. */
  value?: number;
  /** Uit = de code doet niets meer, zonder 'm te hoeven verwijderen (statistieken blijven staan). */
  active: boolean;
  /** Interne omschrijving voor in de admin ("Nieuwsbriefactie augustus"). Nooit publiek. */
  description?: string;
  /** Epoch ms; de code is geldig tot en met dit moment. Leeg = geen einddatum. */
  expiresAt?: number | null;
  /** Minimaal goederen-subtotaal excl. btw waarvoor de code geldt. Leeg/0 = geen ondergrens. */
  minSubtotalCents?: number;
  /** Eén keer per klant inwisselbaar (op account-uid én e-mailadres gecontroleerd). */
  oncePerCustomer?: boolean;
  /** Hoe vaak de code is gebruikt (server telt op bij elke bestelling). */
  usedCount?: number;
  createdAt?: number;
  updatedAt?: number;
}

/** Waarom een code niet geldt. De checkout zet er een nette zin bij (zie `couponRejectionText`). */
export type CouponRejection = "unknown" | "inactive" | "expired" | "min_subtotal" | "already_used" | "no_effect";

/** Wat een geldige code oplevert op déze bestelling. */
export interface CouponBenefit {
  /** Korting op het goederen-subtotaal (leeg bij gratis verzending). */
  discount?: OrderDiscount;
  /** Verzendkosten vervallen. */
  freeShipping: boolean;
  /** Wat de klant er in centen (excl. btw) mee bespaart — ook de maatstaf voor "de gunstigste wint". */
  savingCents: Cents;
}

export type CouponResult = ({ ok: true } & CouponBenefit) | { ok: false; reason: CouponRejection };

/** Codes zijn hoofdletterongevoelig en mogen geen spaties bevatten. */
export function normalizeCouponCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

/** Mag deze tekst een code zijn? (2–24 tekens, letters/cijfers/streepje.) */
export function isValidCouponCode(raw: string): boolean {
  return /^[A-Z0-9-]{2,24}$/.test(normalizeCouponCode(raw));
}

/**
 * Rekent uit wat een code oplevert, of waarom hij niet geldt.
 *
 * `subtotalExcl` is het goederen-subtotaal ná eventuele acties, vóór korting en verzending;
 * `shippingExcl` de berekende verzendkosten (0 bij afhalen — dan levert "gratis verzending"
 * niets op en valt de code af met `no_effect`, zodat we geen loze korting suggereren).
 */
export function evaluateCoupon(
  coupon: Coupon | null | undefined,
  ctx: { subtotalExcl: Cents; shippingExcl: Cents; now?: number; alreadyUsed?: boolean },
): CouponResult {
  if (!coupon) return { ok: false, reason: "unknown" };
  if (coupon.active !== true) return { ok: false, reason: "inactive" };
  const now = ctx.now ?? Date.now();
  if (coupon.expiresAt != null && coupon.expiresAt > 0 && now > coupon.expiresAt) return { ok: false, reason: "expired" };
  if (coupon.oncePerCustomer === true && ctx.alreadyUsed === true) return { ok: false, reason: "already_used" };
  const min = coupon.minSubtotalCents ?? 0;
  if (min > 0 && ctx.subtotalExcl < min) return { ok: false, reason: "min_subtotal" };

  if (coupon.kind === "freeShipping") {
    if (ctx.shippingExcl <= 0) return { ok: false, reason: "no_effect" };
    return { ok: true, freeShipping: true, savingCents: ctx.shippingExcl };
  }

  const value = Number(coupon.value ?? 0);
  if (!(value > 0)) return { ok: false, reason: "no_effect" };
  if (coupon.kind === "percent" && value > 100) return { ok: false, reason: "no_effect" };
  const discount: OrderDiscount = {
    kind: coupon.kind === "percent" ? "percent" : "amount",
    value,
    note: `Kortingscode ${coupon.code}`,
  };
  const saving = discountAmount(ctx.subtotalExcl, discount);
  if (saving <= 0) return { ok: false, reason: "no_effect" };
  return { ok: true, discount, freeShipping: false, savingCents: saving };
}

/**
 * De gunstigste van twee kortingen — een kortingscode stapelt niet met een klant- of
 * staffelkorting. Retourneert wat er op de bestelling terechtkomt.
 *
 * Waarom vergelijken op bespaard bedrag en niet op percentage: een code voor gratis
 * verzending (€ 35,35 glastransport) kan meer waard zijn dan 5% over een kleine order,
 * en andersom. Alleen het eindbedrag zegt iets.
 */
export function bestOfDiscounts(
  customer: OrderDiscount | undefined,
  couponBenefit: CouponBenefit | null,
  ctx: { subtotalExcl: Cents },
): { discount?: OrderDiscount; freeShipping: boolean; couponApplied: boolean } {
  const klant = discountAmount(ctx.subtotalExcl, customer);
  const code = couponBenefit?.savingCents ?? 0;
  if (!couponBenefit || code <= klant) return { discount: customer, freeShipping: false, couponApplied: false };
  return { discount: couponBenefit.discount, freeShipping: couponBenefit.freeShipping, couponApplied: true };
}

/** Nette klanttekst bij een afgewezen code. */
export function couponRejectionText(reason: CouponRejection, minSubtotalCents?: number): string {
  switch (reason) {
    case "unknown":
      return "Deze kortingscode kennen we niet.";
    case "inactive":
      return "Deze kortingscode is niet meer actief.";
    case "expired":
      return "Deze kortingscode is verlopen.";
    case "already_used":
      return "Deze kortingscode hebt u al gebruikt.";
    case "min_subtotal":
      return minSubtotalCents && minSubtotalCents > 0
        ? `Deze code geldt vanaf € ${(minSubtotalCents / 100).toFixed(2).replace(".", ",")} (excl. btw).`
        : "Uw bestelling voldoet niet aan de voorwaarden van deze code.";
    case "no_effect":
    default:
      return "Deze kortingscode levert op deze bestelling niets op.";
  }
}
